import React, { useState, useEffect } from "react";
import Sidebar from "../../components/Sidebar";
import Spinner from "../../components/Spinner";
import {
  fetchRepos,
  fetchCachedRepos,
  fetchUserProviders,
  generatePipeline,
  generateTests
} from "../../api/client";
import "./MainPage.css";

const REPO_SELECTION_LIMIT = 3;

export default function DashboardReposPage() {
  const user = React.useMemo(() => {
    const storedUser = localStorage.getItem('user');
    return storedUser ? JSON.parse(storedUser) : { name: "User" };
  }, []);

  const [providerConnected, setProviderConnected] = useState<boolean | null>(null);

  type Repo = {
    id: string;
    name: string;
    display_name?: string;
    url: string;
    language?: string;
    organization?: string;
    frameworks?: string[];
    test_frameworks?: string[];
    dependency_manager?: string;
  };

  const [repos, setRepos] = useState<Repo[]>([]);
  const [reposLoading, setReposLoading] = useState(true);
  const [refreshingRepos, setRefreshingRepos] = useState(false);

  // Feature toggles and selection
  const [repoFeatures, setRepoFeatures] = useState<{ [repoId: string]: { ci: boolean; tests: boolean; lint: boolean } }>({});
  const [selectedRepoIds, setSelectedRepoIds] = useState<string[]>([]);
  const [limitReached, setLimitReached] = useState(false);
  const [search, setSearch] = useState("");
  const [groupBy, setGroupBy] = useState("language");
  const [showSelected, setShowSelected] = useState(false);
  const [openGroups, setOpenGroups] = useState<string[]>([]);

  // Check provider connection on mount
  useEffect(() => {
    setProviderConnected(null);
    fetchUserProviders()
      .then(resp => setProviderConnected(resp.connected.length > 0))
      .catch(() => setProviderConnected(false));
  }, []);

  // Load repos: try cache first, then background refresh
  useEffect(() => {
    if (providerConnected !== true) return;

    setReposLoading(true);

    fetchCachedRepos()
      .then((cached) => {
        console.log("[fetchCachedRepos] response:", cached);
        if (cached && cached.length > 0) {
          setRepos(cached);
          setReposLoading(false); // Hide spinner right away
          console.log("[setRepos] (from cached):", cached.length);

          // Refresh in background
          setRefreshingRepos(true);
          fetchRepos()
            .then((fresh) => {
              console.log("[fetchRepos] background response:", fresh);
              if (fresh && fresh.length > 0) {
                setRepos(fresh);
                console.log("[setRepos] (from fresh):", fresh.length);
              }
            })
            .catch(console.error)
            .finally(() => setRefreshingRepos(false));
        } else {
          // No cache: Block UI until fresh fetch is done
          fetchRepos()
            .then((fresh) => {
              console.log("[fetchRepos] no cache response:", fresh);
              setRepos(fresh || []);
              console.log("[setRepos] (from fresh no-cache):", fresh ? fresh.length : 0);
            })
            .catch(console.error)
            .finally(() => setReposLoading(false));
        }
      })
      .catch((e) => {
        console.log("[fetchCachedRepos] error:", e);
        setRepos([]);
        setReposLoading(false);
      });
  }, [providerConnected]);

  useEffect(() => {
    console.log("[UI STATE]", { providerConnected, reposLoading, reposLength: repos.length, repos });
  }, [providerConnected, reposLoading, repos]);

  // Filtering/grouping logic
  const filteredRepos = repos.filter(repo =>
    (!showSelected || selectedRepoIds.includes(repo.id)) &&
    (repo.display_name?.toLowerCase().includes(search.toLowerCase()) ||
      repo.language?.toLowerCase().includes(search.toLowerCase()) ||
      (repo.organization || "").toLowerCase().includes(search.toLowerCase()))
  );
  const groupedRepos = filteredRepos.reduce<Record<string, Repo[]>>((acc, repo) => {
    const key = groupBy === "organization" ? (repo.organization || "Other") : (repo.language || "Other");
    if (!acc[key]) acc[key] = [];
    acc[key].push(repo);
    return acc;
  }, {});

  // Selection/feature toggle logic (unchanged)
  const isRepoLocked = (repoId: string) => selectedRepoIds.includes(repoId);
  const disableToggles = (repoId: string) =>
    !isRepoLocked(repoId) && selectedRepoIds.length >= REPO_SELECTION_LIMIT;

  const persistSelectedRepos = (ids: any[] | ((prevState: string[]) => string[])) => {
    setSelectedRepoIds(ids);
    localStorage.setItem('selectedRepoIds', JSON.stringify(ids));
  };

  // Only show the spinner if loading AND repos are not present!
  const showSpinner = providerConnected === null || (reposLoading && repos.length === 0);

  return (
    <div className="dashboard-root">
      <Sidebar user={user} current="repos" />
      <div className="main-content">
        <div className="topbar">
          <h1></h1>
        </div>
        <div className="search-bar">
          <input className="repo-search-input" value={search} onChange={e => setSearch(e.target.value)} placeholder="Search repositories…" />
          <select value={groupBy} onChange={e => setGroupBy(e.target.value)} className="group-dropdown">
            <option value="language">Group by Language</option>
            <option value="organization">Group by Organization</option>
          </select>
          <label className="show-selected-toggle">
            <input type="checkbox" checked={showSelected} onChange={e => setShowSelected(e.target.checked)} />
            Show Selected Only
          </label>
        </div>
        {limitReached && (
          <div className="repo-limit-warning">
            🚫 Repo selection limit reached: You can only select {REPO_SELECTION_LIMIT} repositories this month.
          </div>
        )}
        {/* Spinner: only if loading AND no repos */}
        {showSpinner ? (
          <Spinner
            centered
            style={{ minHeight: 120 }}
            label={
              providerConnected === null
                ? "Checking code host connection..."
                : "Loading repositories..."
            }
          />
        ) : (
          <div>
            {refreshingRepos && (
              <div style={{ textAlign: "center", margin: "12px 0" }}>
                <Spinner centered label="Refreshing…" />
              </div>
            )}
            <div className="repo-group-accordion">
              {Object.entries(groupedRepos).map(([group, repos]) => (
                <div key={group} className="repo-group">
                  <div className="repo-group-header" onClick={() => setOpenGroups((prev) =>
                    prev.includes(group) ? prev.filter(g => g !== group) : [...prev, group]
                  )}>
                    <span className="group-label">{group}</span>
                    <span className="group-arrow">{openGroups.includes(group) ? "▾" : "▸"}</span>
                    <span className="repo-count">{repos.length}</span>
                  </div>
                  {openGroups.includes(group) && (
                    <div className="repo-card-list">
                      {repos.map((repo) => (
                        <div key={repo.id} className="repo-card">
                          <div className="repo-card-header">
                            <a href={repo.url} target="_blank" rel="noopener noreferrer" className="repo-title">{repo.display_name || repo.name}</a>
                            <span className="repo-badge">{repo.language}</span>
                            {(repo.frameworks ?? []).length > 0 && (
                              <span className="repo-badge badge-alt">{(repo.frameworks ?? [])[0]}</span>
                            )}
                          </div>
                          <div className="repo-card-toggles">
                            <div className="feature-row">
                              <span>CI</span>
                              <input type="checkbox" checked={repoFeatures[repo.id]?.ci} disabled={disableToggles(repo.id)} onChange={() => {}} />
                            </div>
                            <div className="feature-row">
                              <span>Tests</span>
                              <input type="checkbox" checked={repoFeatures[repo.id]?.tests} disabled={disableToggles(repo.id)} onChange={() => {}} />
                            </div>
                            <div className="feature-row">
                              <span>Lint</span>
                              <input type="checkbox" checked={repoFeatures[repo.id]?.lint} disabled={disableToggles(repo.id)} onChange={() => {}} />
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
              {Object.keys(groupedRepos).length === 0 && (
                <div className="empty">No repositories found</div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
